/**
 * This is a custom toast component that uses the sonner library.
 * It is based on the shadcn/ui toast component, but with a few changes:
 * - It's styled to match Briefpoint's design system.
 * - Description is placed before the message and styled with a heavy font weight for emphasis.
 * - It does not support toast.loading(), toast.promise(), or toast.custom().
 * - It does not support styling props like className, style, richColors, cancelButtonStyle, or actionButtonStyle.
 *
 * See the sonner docs for more information: https://sonner.emilkowal.ski/toast
 *
 * Example usage:
 *
 * ```tsx
 * toast("Default toast message", {
 *   description: "This is a default toast message",
 * });
 * ```
 */
import clsx from "clsx";
import React from "react";
import {
  CheckCircleFill,
  ExclamationCircleFill,
  ExclamationTriangleFill,
  FlagFill,
  X,
} from "react-bootstrap-icons";
import { type Action, type ExternalToast, toast as sonnerToast } from "sonner";
import styles from "./toast.module.scss";

type ToastVariant = "default" | "success" | "error" | "warning" | "info";

type TitleT = (() => React.ReactNode) | React.ReactNode;

interface ToastProps {
  id: string | number;
  testId?: string;
  message: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  action?: Action | React.ReactNode;
  cancel?: Action | React.ReactNode;
  closeButton?: boolean;
}

interface ToastData extends ExternalToast {
  testId?: string;
}

const isAction = (action: Action | React.ReactNode): action is Action => {
  return typeof action === "object" && action !== null && "label" in action;
};

/**
 * A fully custom toast that still maintains the animations and interactions.
 *
 * Do not use this component directly! Use the toast function instead.
 */
function Toast(props: ToastProps) {
  const {
    message,
    description,
    action,
    cancel,
    id,
    testId,
    variant = "default",
    closeButton = false,
  } = props;

  return (
    <div
      className={clsx(styles.toast, styles[`toast-${variant}`])}
      data-testid={testId}
    >
      <div className={styles["toast-content"]}>
        {variant !== "default" && (
          <div className={styles["toast-icon"]}>
            {variant === "success" && (
              <CheckCircleFill size={18} color="var(--feedback-success-dark)" />
            )}
            {variant === "error" && (
              <ExclamationCircleFill
                size={18}
                color="var(--feedback-error-dark)"
              />
            )}
            {variant === "warning" && (
              <ExclamationTriangleFill
                size={18}
                color="var(--feedback-warning-dark)"
              />
            )}
            {variant === "info" && (
              <FlagFill size={18} color="var(--feedback-info-dark)" />
            )}
          </div>
        )}

        <div>
          {/* Unlike the default toast, description is placed before the message and styled with a heavy font weight for emphasis */}
          {description &&
            (React.isValidElement(description) ? (
              description
            ) : (
              <span className={styles["toast-description"]}>
                {description}{" "}
              </span>
            ))}

          {message}
        </div>
      </div>

      {(action || cancel) && (
        <div className={styles["toast-button-container"]}>
          {isAction(action) ? (
            <button
              type="button"
              className={styles["toast-button"]}
              onClick={(event) => {
                action.onClick(event);
                sonnerToast.dismiss(id);
              }}
            >
              {action.label}
            </button>
          ) : (
            action
          )}

          {isAction(cancel) ? (
            <button
              type="button"
              className={styles["toast-cancel-button"]}
              onClick={(event) => {
                cancel.onClick(event);
                sonnerToast.dismiss(id);
              }}
            >
              {cancel.label}
            </button>
          ) : (
            cancel
          )}
        </div>
      )}

      {closeButton && (
        <button
          type="button"
          className={styles["toast-close-button"]}
          onClick={() => sonnerToast.dismiss(id)}
          aria-label="Close"
        >
          <X size={28} color="var(--text)" />
        </button>
      )}
    </div>
  );
}

// Helper function to create toast with variant
function createToast(
  message: TitleT | React.ReactNode,
  data: ToastData | undefined,
  variant: ToastVariant,
): string | number {
  const { action, cancel, closeButton, description, ...rest } = data || {};

  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        message={typeof message === "function" ? message() : message}
        description={
          typeof description === "function" ? description() : description
        }
        variant={variant}
        action={action}
        cancel={cancel}
        closeButton={closeButton}
      />
    ),
    // pass the remaining props to sonner
    rest,
  );
}

// Main toast function
function toast(message: TitleT, data?: ToastData): string | number {
  return createToast(message, data, "default");
}

// Add variant methods to the toast function
toast.success = (
  message: TitleT | React.ReactNode,
  data?: ToastData,
): string | number => {
  return createToast(message, data, "success");
};

toast.error = (
  message: TitleT | React.ReactNode,
  data?: ToastData,
): string | number => {
  return createToast(message, data, "error");
};

toast.warning = (
  message: TitleT | React.ReactNode,
  data?: ToastData,
): string | number => {
  return createToast(message, data, "warning");
};

toast.info = (
  message: TitleT | React.ReactNode,
  data?: ToastData,
): string | number => {
  return createToast(message, data, "info");
};

export { toast };
